import type { SessionQualityLedger, StickyDowngradePolicy } from '@playcastdotio/PeerCore';

export type RecoveryState = 'normal' | 'recovering' | 'escalated';

/** Depth of the bitrate cut for the current episode. Resolution and framerate are never levers. */
export type RecoveryCutKind = 'soft' | 'hard';

export type RecoverySnapshot = {
  state: RecoveryState;
  maxBitrateBps: number;
  maxFramerate: number;
};

/** Host e2e / Phase 5: recovery exited after an impairment episode (quality SLOs measured after this). */
export type PostClearReadiness = {
  /** True once recovery fully returned to normal after having entered recovering/escalated. */
  recoveryCleared: boolean;
  /** Wall-clock ms when recovery last cleared (if any). */
  clearedAtMs?: number;
  /** Peak cut applied during the last recovery episode (≤ RECOVERY_MAX_BITRATE_BPS). */
  lastRecoveryMaxBitrateBps?: number;
  /** Whether a keyframe was requested during the last recovery climb episode. */
  lastIdrRequested?: boolean;
};

/** One peer's open recovery episode. Mutated in place by the enter, escalate and climb paths. */
export type PeerRecovery = {
  state: RecoveryState;
  /**
   * Fixed for the episode. Selects the exit threshold, so the climb cannot tighten its
   * own exit gate mid-ramp and stall itself.
   */
  episodeCutKind: RecoveryCutKind;
  enteredAt: number;
  healthySince: number | null;
  /** True once a climb-episode IDR was requested for this recovery peer. */
  keyframeSent: boolean;
  maxBitrateBps: number;
  maxFramerate: number;
  rampStep: number;
  /** Bitrate floor at episode enter — exit ramp climbs from this (not always hard-cut 1.5 Mbps). */
  cutFloorBitrateBps: number;
  /**
   * Wall-clock ms when standing queue first crossed SUSTAINED_STANDING_QUEUE_MS_ENTER
   * during the current elevated (soft) cut. Null while the queue is below the enter
   * threshold or once the cut is already hard. Drives the elevated→hard escalation.
   */
  sustainedQueueSince: number | null;
  /**
   * Wall-clock ms of the window transition (minimize/restore/maximize) this episode was
   * entered during, if any. Stamped ONCE at entry so a later window event cannot overwrite
   * the association — deriving it from the guard's mutable clock lost it, and the cut this
   * PR exists to release was then never released by the window path.
   */
  transitionAtMs?: number;
};

export type RecoveryEpisodeStoreDeps = {
  /** Within-tier encoding only: bitrate + fps. Resolution stays locked. */
  applyEncoding: (peerId: string, opts: { maxBitrateBps: number; maxFramerate: number }) => void;
  /** Read for the ledger's tier column only; the store never raises or releases a floor. */
  stickyDowngrade?: StickyDowngradePolicy;
  qualityLedger?: SessionQualityLedger;
};

/**
 * RecoveryEpisodeStore — the per-peer recovery episode records and the one act of applying
 * an episode's encoding to the sender (with the ledger entry that goes with it).
 *
 * Holds no policy: which cut to take, when to escalate and how to climb are decided by the
 * recovery manager and its climb manager; this class is where the resulting record lives so
 * both read and mutate the same object.
 */
export class RecoveryEpisodeStore {
  private readonly peers = new Map<string, PeerRecovery>();
  private readonly postClearByPeer = new Map<string, PostClearReadiness>();

  constructor(private readonly deps: RecoveryEpisodeStoreDeps) {}

  public get(peerId: string): PeerRecovery | undefined {
    return this.peers.get(peerId);
  }

  public isNormal(peerId: string): boolean {
    const peer = this.peers.get(peerId);
    return !peer || peer.state === 'normal';
  }

  public stateOf(peerId: string): RecoveryState {
    return this.peers.get(peerId)?.state ?? 'normal';
  }

  /** Open a new episode: forget any earlier post-clear record, store, and apply. */
  public begin(peerId: string, peer: PeerRecovery): void {
    this.postClearByPeer.delete(peerId);
    this.peers.set(peerId, peer);
    this.apply(peerId, peer);
  }

  /** Store an episode record without applying it (restore paths apply explicitly). */
  public set(peerId: string, peer: PeerRecovery): void {
    this.peers.set(peerId, peer);
  }

  /** Push the episode's encoding to the sender and record it on the ledger. */
  public apply(peerId: string, peer: PeerRecovery): void {
    this.deps.applyEncoding(peerId, {
      maxBitrateBps: peer.maxBitrateBps,
      maxFramerate: peer.maxFramerate,
    });
    try {
      const floor = this.deps.stickyDowngrade?.floorFor(peerId) ?? 'none';
      this.deps.qualityLedger?.record(peerId, {
        maxBitrateBps: peer.maxBitrateBps,
        maxFramerate: peer.maxFramerate,
        tier: floor === 'tier360' ? 'bottom' : floor === 'none' ? 'none' : floor,
      });
    } catch {
      // Ledger must never take recovery down.
    }
  }

  /** Close the episode: the peer returns to normal and its post-clear readiness is recorded. */
  public end(peerId: string, readiness: PostClearReadiness): void {
    this.postClearByPeer.set(peerId, readiness);
    this.peers.delete(peerId);
  }

  public postClearFor(peerId: string): PostClearReadiness {
    return this.postClearByPeer.get(peerId) ?? { recoveryCleared: false };
  }

  /** The episode's encoding, or `whenNormal` for a peer with no open episode. */
  public snapshot(peerId: string, whenNormal: { maxBitrateBps: number; maxFramerate: number }): RecoverySnapshot {
    const peer = this.peers.get(peerId);
    if (!peer) return { state: 'normal', ...whenNormal };
    return { state: peer.state, maxBitrateBps: peer.maxBitrateBps, maxFramerate: peer.maxFramerate };
  }

  public clearPeer(peerId: string): void {
    this.peers.delete(peerId);
    this.postClearByPeer.delete(peerId);
  }

  public clearAll(): void {
    this.peers.clear();
    this.postClearByPeer.clear();
  }
}
